import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import BasicTemplate from 'components/Toast/BasicTemplate';
import { post, patch } from 'services/permissions/userPermissionServices';
import PermissionSetupDropDown from 'components/dropdowns/PermissionSetupDropDown';
import UserDropdown from 'components/dropdowns/UserDropdown';
import { getAllEvaUsers } from 'services/userServices';

const newItem = {
  id: 0,
  evaUserId: null,
  permissionSetupId: null,
  viewPermission: false,
  createPermission: false,
  updatePermission: false,
  deletePermission: false
};

const UserPermissionModal = ({ data, isShow, handleClose, handleSubmit }) => {
  const [formData, setFormData] = useState(newItem);
  const [isWaitingSave, setIsWaitingSave] = useState(false);
  const [users, setUsers] = useState([]);
  const isEdit = formData?.id > 0;

  useEffect(() => {
    setFormData(data && data.id > 0 ? data : newItem);
  }, [data, isShow]);

  useEffect(() => {
    const controller = new AbortController();
    getAllEvaUsers(controller.signal)
      .then(res => {
        if (res?.length) setUsers(res);
      })
      .catch(err => {
        console.log('Error in user fetch', err);
      });

    return () => {
      controller.abort();
    };
  }, []);

  const evaUserName =
    users.find(u => u.value === formData?.evaUserId)?.label ?? '';

  const checkChange = (e, key) => {
    const value = e.target.checked;
    setFormData({ ...formData, [key]: value });
  };

  const singleSelectChange = (key, option) => {
    setFormData({ ...formData, [key]: option?.value ?? null });
  };

  const onSubmit = async event => {
    event.preventDefault();
    setIsWaitingSave(true);

    if (formData.id <= 0 || formData.id == undefined) {
      if (!formData.evaUserId) {
        toast.error(
          <BasicTemplate success={false} text={'Please select user'} />
        );
        setIsWaitingSave(false);
        return;
      }
      if (!formData.permissionSetupId) {
        toast.error(
          <BasicTemplate success={false} text={'Please select Permisssion'} />
        );
        setIsWaitingSave(false);
        return;
      }
    }

    const result =
      formData.id > 0
        ? await patch(formData, formData.id)
        : await post(formData);

    const message =
      result.message ??
      'There was a problem saving the data. Please check your entires and try again.';
    result.success
      ? toast.success(
          <BasicTemplate
            success={result.success}
            text="User Permission successfully saved!"
          />
        )
      : toast.error(<BasicTemplate success={result.success} text={message} />);
    setIsWaitingSave(false);

    if (result.success) {
      handleSubmit();
    }
  };

  return (
    <Modal
      show={isShow}
      onHide={handleClose}
      contentClassName="border"
      centered
    >
      <Form onSubmit={onSubmit}>
        <Modal.Header closeButton className="bg-light px-card border-bottom-0">
          <Modal.Title as="h5">
            {isEdit ? 'Edit User Permission' : 'Create User Permission'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-card">
          <Row className="mb-3">
            <Form.Group as={Col} md="12" className="mb-3" controlId="evaUser">
              <Form.Label>Eva User</Form.Label>
              {isEdit ? (
                <div className="fw-bold">{evaUserName || '—'}</div>
              ) : (
                <UserDropdown
                  isMulti={false}
                  showLogin
                  isClearable
                  selectedOptions={formData?.evaUserId}
                  handleChange={option =>
                    setFormData({
                      ...formData,
                      evaUserId: option?.value ?? null
                    })
                  }
                />
              )}
            </Form.Group>
            <Form.Group as={Col} md="12" controlId="permissionSetup">
              <Form.Label>Permission</Form.Label>
              <PermissionSetupDropDown
                selectedOptions={formData?.permissionSetupId}
                handleChange={opts =>
                  singleSelectChange('permissionSetupId', opts)
                }
                isMulti={false}
                isDisabled={isEdit}
              />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group as={Col} md="6" className="mb-2">
              <Form.Check
                type="checkbox"
                id="viewCheck"
                label="View"
                onChange={e => checkChange(e, 'viewPermission')}
                checked={formData?.viewPermission ?? false}
              />
            </Form.Group>
            <Form.Group as={Col} md="6" className="mb-2">
              <Form.Check
                type="checkbox"
                id="createCheck"
                label="Create"
                onChange={e => checkChange(e, 'createPermission')}
                checked={formData?.createPermission ?? false}
              />
            </Form.Group>
            <Form.Group as={Col} md="6">
              <Form.Check
                type="checkbox"
                id="updateCheck"
                label="Update"
                onChange={e => checkChange(e, 'updatePermission')}
                checked={formData?.updatePermission ?? false}
              />
            </Form.Group>
            <Form.Group as={Col} md="6">
              <Form.Check
                type="checkbox"
                id="deleteCheck"
                label="Delete"
                onChange={e => checkChange(e, 'deletePermission')}
                checked={formData?.deletePermission ?? false}
              />
            </Form.Group>
          </Row>
        </Modal.Body>
        <Modal.Footer className="bg-light px-card border-top-0">
          <Button
            variant="primary"
            type="submit"
            disabled={isWaitingSave}
            className="px-4 mx-0"
          >
            {!isWaitingSave ? (
              'Save'
            ) : (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                <span> ...Loading</span>
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

UserPermissionModal.propTypes = {
  data: PropTypes.object,
  isShow: PropTypes.bool,
  handleClose: PropTypes.func,
  handleSubmit: PropTypes.func
};

export default UserPermissionModal;
